import { useEffect, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

interface BusinessSearchBarProps {
  search: string
  isActive: 'all' | 'active' | 'inactive'
  onSearchChange: (value: string) => void
  onActiveChange: (value: 'all' | 'active' | 'inactive') => void
}

/**
 * Búsqueda con debounce: el input es controlado por la URL (prop `search`),
 * pero los cambios del usuario se acumulan localmente y se propagan al padre
 * sólo cuando para de tipear ~300ms. Si la URL cambia externamente (limpiar
 * filtros, navegación), el `key` remonta el input para que tome el nuevo valor.
 */
export function BusinessSearchBar({
  search,
  isActive,
  onSearchChange,
  onActiveChange,
}: BusinessSearchBarProps) {
  const [draft, setDraft] = useState(search)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const handleInput = (value: string) => {
    setDraft(value)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      onSearchChange(value)
    }, 300)
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-[240px] flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
        <Input
          key={search /* remount cuando la URL cambia externamente */}
          defaultValue={draft}
          onChange={(e) => handleInput(e.target.value)}
          placeholder="Buscar negocio..."
          className="pl-9"
        />
      </div>
      <Select value={isActive} onValueChange={(v) => onActiveChange(v as typeof isActive)}>
        <SelectTrigger className="w-[180px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos</SelectItem>
          <SelectItem value="active">Activos</SelectItem>
          <SelectItem value="inactive">Inactivos</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
